import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

const PRODUCT_BREADCRUMB_STORAGE_KEY = 'tilray:product-breadcrumb-collection';

/**
 * @typedef {object} ProductBreadcrumbCollectionContext
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 */

/** @param {DOMStringMap} dataset */
function getCollectionContextFromDataset(dataset) {
  const { collectionHandle, collectionTitle, collectionUrl } = dataset;

  if (!collectionHandle || !collectionTitle || !collectionUrl) {
    return null;
  }

  return {
    handle: collectionHandle,
    title: collectionTitle,
    url: collectionUrl,
  };
}

/** @param {ProductBreadcrumbCollectionContext | null} collectionContext */
function persistCollectionContext(collectionContext) {
  if (!collectionContext) return;

  const serializedContext = JSON.stringify({
    ...collectionContext,
    url: window.location.href || collectionContext.url,
  });

  try {
    window.sessionStorage.setItem(PRODUCT_BREADCRUMB_STORAGE_KEY, serializedContext);
  } catch {}

  try {
    window.localStorage.setItem(PRODUCT_BREADCRUMB_STORAGE_KEY, serializedContext);
  } catch {}
}

/**
 * @typedef {Object} CustomProductCardRefs
 * @property {HTMLElement} [expandPanel]
 * @property {HTMLButtonElement} [buyToggle]
 * @property {HTMLButtonElement} [addToCart]
 * @property {HTMLInputElement} [qtyInput]
 * @property {HTMLElement} [priceAmount]
 * @property {HTMLElement} [lineTotal]
 * @property {HTMLInputElement} [compareCheckbox]
 * @property {HTMLElement} [compareLabel]
 * @property {HTMLElement} [buyButtonText]
 * @property {HTMLInputElement[]} [variantInputs]
 * @property {HTMLButtonElement} [favouriteButton]
 */

/**
 * Custom product card with expandable buy panel, variant picker, qty selector
 * and add to cart. Supports modular block-based architecture.
 *
 * @extends {Component<CustomProductCardRefs>}
 */
class CustomProductCardElement extends Component {
  // No required refs - all elements are optional for modular flexibility
  /** @type {string[]} */
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();
    this.#syncSelectedFormat();
    this.#updateLineTotal();
    this.#syncCompareState();

    // Re-sync when compare store updates (e.g. navigated to a new page while
    // products from a previous PLP are still stored).
    this._compareListener = () => this.#syncCompareState();
    document.addEventListener('compare:updated', this._compareListener);

    // Listen for variant changes from child blocks
    /** @type {EventListener} */
    this._variantListener = (e) => this.#handleVariantChanged(/** @type {CustomEvent} */ (e));
    this.addEventListener('variant:changed', this._variantListener);

    // Bind click handlers for buy toggle and close buttons
    this.#bindExpandHandlers();

    // Bind card click for navigation
    this.#bindCardClick();
  }

  /**
   * Bind click handlers for expand/collapse functionality
   */
  #bindExpandHandlers() {
    // Buy toggle buttons (expand the card)
    const buyToggleBtns = this.querySelectorAll('.cpc__buy-toggle');
    buyToggleBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleBuyClick();
      });
    });

    // Close buttons (collapse the card)
    const closeBtns = this.querySelectorAll('.cpc__close-toggle, [ref="closeButton"]');
    closeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleCollapse();
      });
    });
  }

  /**
   * Bind card click for navigation to PDP
   * Excludes buttons, inputs, labels, and interactive elements
   */
  #bindCardClick() {
    this.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;

      const targetLink = e.target.closest('a');
      if (targetLink instanceof HTMLAnchorElement && targetLink.href) {
        persistCollectionContext(getCollectionContextFromDataset(this.dataset));
        return;
      }

      const isInteractive = e.target.closest('button, input, label, select, textarea, .cpc__interactive, [role="button"]');
      if (isInteractive) return;

      const productUrl = this.dataset.productUrl;
      if (productUrl) {
        persistCollectionContext(getCollectionContextFromDataset(this.dataset));
        window.location.href = productUrl;
      }
    });
  }

  disconnectedCallback() {
    if (this._compareListener) {
      document.removeEventListener('compare:updated', this._compareListener);
    }
    if (this._variantListener) {
      this.removeEventListener('variant:changed', this._variantListener);
    }
  }

  /**
   * Handle buy button click - expands or adds directly based on settings
   */
  handleBuyClick() {
    const expandBehavior = this.dataset.expandBehavior || 'expand';
    
    if (expandBehavior === 'direct') {
      this.handleAddToCart();
    } else {
      this.handleExpand();
    }
  }

  /** Expand the card to show additional blocks (formats, purchase, etc.) */
  handleExpand() {
    this.#runWithoutCarouselScrollShift(() => {
      this.dataset.expanded = 'true';
      this.querySelectorAll('custom-buy-button[data-fetch-base-prices="on-expand"]').forEach((button) => {
        if (typeof button.loadBasePricesOnExpand === 'function') {
          button.loadBasePricesOnExpand();
        }
      });
    });
  }

  /** Collapse the card to hide expanded content */
  handleCollapse() {
    this.#runWithoutCarouselScrollShift(() => {
      delete this.dataset.expanded;
    });
  }

  /** @returns {HTMLElement | null} */
  #getCarouselScroller() {
    const slideshow = this.closest('slideshow-component');
    if (!slideshow) return null;

    const scroller = slideshow.querySelector('slideshow-slides');
    return scroller instanceof HTMLElement ? scroller : null;
  }

  /**
   * Expand/collapse changes card height inside carousels, which can trigger
   * scroll-snap realignment. Preserve horizontal scroll while layout settles.
   * @param {() => void} action
   */
  #runWithoutCarouselScrollShift(action) {
    const scroller = this.#getCarouselScroller();
    if (!scroller) {
      action();
      return;
    }

    const scrollLeft = scroller.scrollLeft;
    scroller.style.scrollSnapType = 'none';

    action();

    const restoreScroll = () => {
      scroller.scrollLeft = scrollLeft;
    };

    restoreScroll();
    requestAnimationFrame(() => {
      restoreScroll();
      requestAnimationFrame(restoreScroll);
    });

    const resizeObserver = new ResizeObserver(() => {
      restoreScroll();
    });

    resizeObserver.observe(this);

    window.setTimeout(() => {
      resizeObserver.disconnect();
      scroller.style.scrollSnapType = '';
      restoreScroll();
    }, 500);
  }

  /**
   * Handle a variant selection change.
   * @param {Event} event
   */
  handleVariantChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.checked) return;

    this.#syncSelectedFormat();
    this.#updateLineTotal();

    // Update top-level price (above the divider) to match selected variant.
    const formatted = target.dataset.priceFormatted;
    if (formatted && this.refs.priceAmount) {
      this.refs.priceAmount.textContent = formatted;
    }
  }

  /** Increase qty by 1. */
  increaseQty() {
    const input = this.refs.qtyInput;
    if (!input) return;
    input.value = String(Math.max(1, this.#qty() + 1));
    this.handleQtyChange();
  }

  /** Decrease qty by 1 (min 1). */
  decreaseQty() {
    const input = this.refs.qtyInput;
    if (!input) return;
    input.value = String(Math.max(1, this.#qty() - 1));
    this.handleQtyChange();
  }

  /** Update displayed line total when qty changes. */
  handleQtyChange() {
    this.#updateLineTotal();
  }

  /** Add the selected variant + qty to the cart. */
  handleAddToCart() {
    const variantId = this.#selectedVariantId();
    const quantity = this.#qty();

    if (!variantId) return;

    const button = this.refs.addToCart;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const cartItemSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', String(quantity));
    if (cartItemSectionIds.length) body.append('sections', cartItemSectionIds.join(','));

    fetch(`${window.Theme?.routes?.cart_add_url ?? '/cart/add'}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    })
      .then(async (response) => {
        const data = await response.json();

        if (!response.ok) {
          const buyButtonText = this.refs.buyButtonText;
          if (buyButtonText) buyButtonText.textContent = data.message || 'Error';
          setTimeout(() => {
            if (buyButtonText) buyButtonText.textContent = button?.dataset.label || 'Buy';
          }, 2500);
          return;
        }

        const isPartialAdd = data.status && data.status !== 200;

        if (isPartialAdd) {
          const buyButtonText = this.refs.buyButtonText;
          if (buyButtonText) buyButtonText.textContent = data.message || 'Error';
          setTimeout(() => {
            if (buyButtonText) buyButtonText.textContent = button?.dataset.label || 'Buy';
          }, 2500);
        } else {
          // Animate the checkmark
          this.#animateAddToCart();
        }

        // Notify the rest of the theme so the cart icon / drawer refreshes.
        // Dispatched for both full and partial adds since items were added to cart.
        this.dispatchEvent(
          new CartAddEvent(undefined, this.id, {
            source: 'custom-product-card',
            productId: this.dataset.productId,
            variantId,
            itemCount: quantity,
            sections: data?.sections,
          })
        );

        // Reset qty.
        if (this.refs.qtyInput) this.refs.qtyInput.value = '1';
        this.#updateLineTotal();
      })
      .catch((err) => {
        console.error('[custom-product-card] add to cart failed', err);
      })
      .finally(() => {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
  }

  /** Add or remove this product from the compare store. */
  handleCompareToggle(event) {
    const checkbox = /** @type {HTMLInputElement} */ (event.target);
    const store = window.CompareStore;
    if (!store) return;

    if (checkbox.checked) {
      let payload;
      try {
        // Script is inside this custom-product-card element
        let scriptEl = this.querySelector('.js-compare-payload');
        // Fallback: look in parent container (in case it rendered as sibling)
        if (!scriptEl) {
          const container = this.closest('.product-grid__item') || this.parentElement;
          scriptEl = container ? container.querySelector('.js-compare-payload') : null;
        }
        const raw = scriptEl ? scriptEl.textContent : null;
        payload = raw ? JSON.parse(raw) : null;
        // Build metafields from .js-compare-row-data scripts (rendered by _compare-row blocks)
        if (payload) {
          const rowScripts = this.querySelectorAll('.js-compare-row-data');
          const metafields = {};
          const metafieldImages = {};
          rowScripts.forEach((s) => {
            try {
              const row = JSON.parse(s.textContent);
              if (row.metafield_key) {
                metafields[row.metafield_key] = row.value || '';
                if (row.image) metafieldImages[row.metafield_key] = row.image;
              }
            } catch (_) {}
          });
          payload.metafields = metafields;
          payload.metafield_images = metafieldImages;
        }
      } catch (e) {
        console.error('[compare] payload parse error', e);
        payload = null;
      }
      if (!payload) {
        // Fallback minimal payload
        payload = { id: Number(this.dataset.productId), title: this.dataset.productHandle };
      }
      const added = store.add(payload);
      if (!added) {
        checkbox.checked = false;
      }
    } else {
      store.remove(this.dataset.productId);
    }
  }

  /**
   * Handle favourite toggle - currently just toggles UI state.
   * Actual functionality to be handled by external app.
   */
  handleFavouriteToggle() {
    const button = this.refs.favouriteButton;
    if (!button) return;
    
    const isActive = button.dataset.active === 'true';
    button.dataset.active = isActive ? 'false' : 'true';
    
    // Dispatch custom event for external apps to hook into
    this.dispatchEvent(new CustomEvent('favourite:toggle', {
      bubbles: true,
      detail: {
        productId: this.dataset.productId,
        active: !isActive
      }
    }));
  }

  // ---- internals ----

  /**
   * Handle variant changed event from child blocks (e.g., custom-variant-formats)
   * @param {CustomEvent} event
   */
  #handleVariantChanged(event) {
    const { variantId, price, priceFormatted, available } = event.detail || {};
    
    // Update price display
    if (priceFormatted && this.refs.priceAmount) {
      this.refs.priceAmount.textContent = priceFormatted;
    }
    
    // Update line total
    this.#updateLineTotal();
    
    // Update buy button state
    const buyButton = this.refs.addToCart || this.refs.buyToggle;
    if (buyButton && available !== undefined) {
      buyButton.disabled = !available;
    }
  }

  /** Public wrapper — called by product-compare.js boot and MutationObserver. */
  syncCompareState() {
    this.#syncCompareState();
  }

  /** Sync compare checkbox with current sessionStorage state. */
  #syncCompareState() {
    const checkbox = this.refs.compareCheckbox;
    if (!checkbox || !window.CompareStore) return;
    const isCompared = window.CompareStore.has(this.dataset.productId);
    checkbox.checked = isCompared;
  }


  /** @type {number | null} */
  #resetTimeout = null;

  /** Animates the checkmark on the add to cart button. */
  #animateAddToCart() {
    const button = this.refs.addToCart;
    if (!button) return;

    // Clear any existing timeout
    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
      this.#resetTimeout = null;
    }

    // Set added state to trigger CSS animation
    button.dataset.added = 'true';

    // Reset after animation completes
    this.#resetTimeout = setTimeout(() => {
      button.removeAttribute('data-added');
      this.#resetTimeout = null;
    }, 1500);
  }

  /** @returns {number} */
  #qty() {
    const value = parseInt(this.refs.qtyInput?.value ?? '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** @returns {string | null} */
  #selectedVariantId() {
    const inputs = this.#variantInputs();
    const checked = inputs.find((i) => i.checked);
    return checked ? checked.value : null;
  }

  /** @returns {HTMLInputElement[]} */
  #variantInputs() {
    const refInputs = this.refs.variantInputs;
    if (Array.isArray(refInputs)) return refInputs;
    if (refInputs instanceof HTMLInputElement) return [refInputs];
    return Array.from(this.querySelectorAll(`input[name^="variant-"]`));
  }

  /** Update is-selected class on format labels based on checked input. */
  #syncSelectedFormat() {
    const inputs = this.#variantInputs();
    for (const input of inputs) {
      const label = input.closest('.custom-product-card__format');
      if (!label) continue;
      label.classList.toggle('is-selected', input.checked);
    }
  }

  /** Update the line total (price * qty) using the selected variant. */
  #updateLineTotal() {
    const lineTotal = this.refs.lineTotal;

    const checked = this.#variantInputs().find((i) => i.checked);
    if (!checked) return;

    const priceCents = parseInt(checked.dataset.price ?? '0', 10);
    const total = priceCents * this.#qty();
    const formattedTotal = this.#formatMoney(total);

    if (lineTotal) {
      lineTotal.textContent = formattedTotal;
    }
  }

  /**
   * Minimal money formatter: respects existing variant's formatted price by
   * substituting the numeric portion. Falls back to "$X.XX".
   * @param {number} cents
   */
  #formatMoney(cents) {
    const checked = this.#variantInputs().find((i) => i.checked);
    const sample = checked?.dataset.priceFormatted;
    const amount = (cents / 100).toFixed(2);

    if (sample) {
      // Replace any number group in the formatted sample with our new amount.
      const replaced = sample.replace(/[\d,.]+/, amount);
      if (replaced !== sample) return replaced;
    }
    return `$${amount}`;
  }
}

if (!customElements.get('custom-product-card')) {
  customElements.define('custom-product-card', CustomProductCardElement);
}
